import React, { useState, useEffect } from "react";
import { Box, Button, Flex, Table, Typography } from "flux";
import { useParams } from "react-router-dom";
import { useFileDownload } from "@adora/shared/hooks/use-file-download";
import { resizeImages } from "../../utils/resizer";
import { useToast } from "@adora/shared/hooks/use-toast";
import { OverlayImage } from "@adora/shared/components/overlay-image";
import { useAdBuilderImages, useMutateAdBuilderImages } from "../../hooks/use-ad-builder-images";
import { AspectRatio, findTemplate } from "@adora/shared";
import { AdBuilderServerImage } from "@adora/shared/types/ad-builder-image";
import { BulkUpdateAdBuilderImagesDialog } from "./upload-edited-ad-images-dialog";
import { useDialog } from "@adora/shared/hooks/use-dialog";
import { createDownloadTrackingCallbacks } from "@web/utils";

export interface OriginalAdImagesTableProps {
  adAssetGroupId: number;
}

const OriginalAdImagesTable: React.FC<OriginalAdImagesTableProps> = ({ adAssetGroupId }) => {
  const { brandId } = useParams<{ brandId: string }>();
  const [canResize, setCanResize] = useState<boolean>(true);
  const [canDownloadResized, setCanDownloadResized] = useState<boolean>(false);
  const { images, isLoading } = useAdBuilderImages(adAssetGroupId);
  const { downloadAdBuilderImages } = useMutateAdBuilderImages();
  const { downloadFile, isDownloading } = useFileDownload();
  const { openToast } = useToast();

  useEffect(() => {
    setCanDownloadResized(images.length > 1); // TODO: Figure out what this should actually be
  }, [images]);

  const { openDialog } = useDialog();

  async function handleDownloadAll() {
    await downloadFile(() => downloadAdBuilderImages(adAssetGroupId), {
      fileName: "ad-images",
      fileType: "zip",
      ...createDownloadTrackingCallbacks({
        feature: "internal_ad_builder_images",
        download_type: "bulk",
        ad_asset_group_id: adAssetGroupId,
        resized: false
      }),
      onError: (error) => {
        console.error("Download failed:", error);
      }
    });
  }

  const handleBulkUploadAdBuilderImages = async (adAssetGroupId: number) => {
    if (!adAssetGroupId) return;
    await openDialog(BulkUpdateAdBuilderImagesDialog, {
      adAssetGroupId
    });
  };

  const ImagePreviewCell: React.FC<{
    image_url?: string;
    top_left_coordinates?: number[];
    bottom_right_coordinates?: number[];
  }> = ({ image_url, top_left_coordinates, bottom_right_coordinates }) => {
    if (!image_url) return <Typography color="textSecondary">No image</Typography>;

    if (top_left_coordinates && bottom_right_coordinates) {
      return (
        <svg>
          <defs>
            <clipPath id="myClip">
              <rect
                x={top_left_coordinates[0]}
                y={top_left_coordinates[1]}
                width={bottom_right_coordinates[0] - top_left_coordinates[0]}
                height={bottom_right_coordinates[1] - top_left_coordinates[1]}
              />
            </clipPath>
          </defs>
          <image
            xlinkHref={image_url}
            clipPath="url(#myClip)"
            preserveAspectRatio="xMinYMin slice"
            transform={`scale(${150 / (bottom_right_coordinates[0] - top_left_coordinates[0])})`} // Scale factor: 150/2048 ≈ 0.073
          />
        </svg>
      );
    } else {
      return <img src={image_url} alt="Preview" width={150} height={150} style={{ objectFit: "contain" }} />;
    }
  };

  const handleResizeAll = async () => {
    handleResize();
  };

  const handleResize = async (image?: AdBuilderServerImage) => {
    if (!brandId) return;

    setCanResize(false);
    const response = await resizeImages({
      adAssetGroup: adAssetGroupId,
      adImageIds: image ? [image.ad_image_id] : images.map((image) => image.ad_image_id),
      brandId: Number.parseInt(brandId)
    });
    response.match(
      (error) => {
        if (error.tag == "validationError") {
          error.errors.map((message) => openToast(message));
        } else {
          openToast("An unknown error occured");
        }
      },
      () => {
        openToast("Job submitted successfully");
      }
    );
    setCanResize(true);
  };

  const handleDownloadResized = async () => {
    if (!brandId) return;
    setCanResize(false);
    setCanDownloadResized(false);
    await downloadFile(() => downloadAdBuilderImages(adAssetGroupId, true), {
      fileName: "ad-images",
      fileType: "zip",
      ...createDownloadTrackingCallbacks({
        feature: "internal_ad_builder_images",
        download_type: "bulk",
        ad_asset_group_id: adAssetGroupId,
        resized: true
      }),
      onError: (error) => {
        console.error("Download failed:", error);
      }
    });
    setCanResize(true);
    setCanDownloadResized(true);
  };

  const columns = [
    { field: "id", value: "Ad Builder ID", width: 80 },
    {
      field: "src",
      value: "Image thumbnail",
      width: 200,
      renderCell: (row: AdBuilderServerImage) => {
        return <ImagePreviewCell image_url={row.ad_image_url} />;
      }
    },
    {
      field: "sizes",
      value: "Resized Image thumbnail",
      width: 200,
      renderCell: (row: AdBuilderServerImage) => {
        if (row.beauty_image_url) {
          return (
            <div>
              <Box>
                <ImagePreviewCell image_url={row.beauty_image_url} />
              </Box>
              <Box>
                <Button
                  variant="outlined"
                  disabled={isDownloading}
                  size="small"
                  onClick={() => {
                    handleResize(row);
                  }}
                  dataDogIdentifier="original ad images table regenerate resize"
                >
                  Regenerate Resize
                </Button>
              </Box>
            </div>
          );
        }
        return <Typography color="textSecondary">No resized image</Typography>;
      }
    },
    {
      field: "landscape",
      value: "Landscape image",
      width: 200,
      renderCell: (row: AdBuilderServerImage) => {
        const landscape_size = findTemplate(row.template_sizes, { kind: "aspectRatio", aspectRatio: "landscape" });

        if (landscape_size) {
          return (
            <div>
              <Box>
                <OverlayImage
                  baseImage={
                    <img
                      src={landscape_size.cropped_image_url || ""}
                      alt="Preview"
                      width={150}
                      height={150}
                      style={{ objectFit: "contain" }}
                    />
                  }
                  overlayImage={landscape_size.template_image_url || ""}
                  height="150px"
                  width="150px"
                  displayOverlay={true}
                  aspectRatio={landscape_size.type as AspectRatio}
                />
              </Box>
            </div>
          );
        }
        return <Typography color="textSecondary">No landscape image</Typography>;
      }
    },
    {
      field: "portrait",
      value: "Portrait image",
      width: 200,
      renderCell: (row: AdBuilderServerImage) => {
        const portrait_size = findTemplate(row.template_sizes, { kind: "aspectRatio", aspectRatio: "portrait" });

        if (portrait_size) {
          return (
            <div>
              <Box>
                <OverlayImage
                  baseImage={
                    <img
                      src={portrait_size.cropped_image_url || ""}
                      alt="Preview"
                      width={150}
                      height={150}
                      style={{ objectFit: "contain" }}
                    />
                  }
                  overlayImage={portrait_size.template_image_url || ""}
                  height="150px"
                  width="150px"
                  displayOverlay={true}
                  aspectRatio={portrait_size.type as AspectRatio}
                />
              </Box>
            </div>
          );
        }
        return <Typography color="textSecondary">No portrait image</Typography>;
      }
    },
    {
      field: "feed",
      value: "Feed image",
      width: 200,
      renderCell: (row: AdBuilderServerImage) => {
        const feed_size = findTemplate(row.template_sizes, { kind: "aspectRatio", aspectRatio: "feed" });

        if (feed_size) {
          return (
            <div>
              <Box>
                <OverlayImage
                  baseImage={
                    <img
                      src={feed_size.cropped_image_url || ""}
                      alt="Preview"
                      width={150}
                      height={150}
                      style={{ objectFit: "contain" }}
                    />
                  }
                  overlayImage={feed_size.template_image_url || ""}
                  height="150px"
                  width="150px"
                  displayOverlay={true}
                  aspectRatio={feed_size.type as AspectRatio}
                />
              </Box>
            </div>
          );
        }
        return <Typography color="textSecondary">No feed image</Typography>;
      }
    },
    {
      field: "sqare",
      value: "Square image",
      width: 200,
      renderCell: (row: AdBuilderServerImage) => {
        const square_size = findTemplate(row.template_sizes, { kind: "aspectRatio", aspectRatio: "square" });

        if (square_size) {
          return (
            <div>
              <Box>
                <OverlayImage
                  baseImage={
                    <img
                      src={square_size.cropped_image_url || ""}
                      alt="Preview"
                      width={150}
                      height={150}
                      style={{ objectFit: "contain" }}
                    />
                  }
                  overlayImage={square_size.template_image_url || ""}
                  height="150px"
                  width="150px"
                  displayOverlay={true}
                  aspectRatio={square_size.type as AspectRatio}
                />
              </Box>
            </div>
          );
        }
        return <Typography color="textSecondary">No square image</Typography>;
      }
    }
  ];

  return (
    <>
      <Typography variant="h2" my={2}>
        Original Ad Images (New)
      </Typography>
      <Flex justifyContent="space-between" alignItems="center" mb={4}>
        <Box flex="1">
          <Button
            variant="contained"
            disabled={isDownloading}
            onClick={handleDownloadAll}
            dataDogIdentifier="original ad images table download all"
          >
            Download All
          </Button>
          <Button
            variant="contained"
            sx={{ marginLeft: "16px" }}
            disabled={!canResize}
            onClick={handleResizeAll}
            dataDogIdentifier="original ad images table resize all"
          >
            Resize All
          </Button>
          <Button
            variant="contained"
            sx={{ marginLeft: "16px" }}
            disabled={!canDownloadResized}
            onClick={handleDownloadResized}
            dataDogIdentifier="original ad images table download all resized images"
          >
            Download All Resized Images
          </Button>
          <Button
            variant="contained"
            sx={{ marginLeft: "16px" }}
            onClick={() => handleBulkUploadAdBuilderImages(adAssetGroupId)}
            dataDogIdentifier="original ad images table upload edited ad builder images (bulk)"
          >
            Upload Edited Ad Builder Images (bulk)
          </Button>
        </Box>
        <Box ml="auto">
          <Typography variant="body1">Total Ad Images: {images.length}</Typography>
        </Box>
      </Flex>
      {images.length > 0 ? (
        <Table columns={columns} rows={images} isLoading={isLoading} dataDogIdentifier="original ad images" />
      ) : (
        <Typography color="textSecondary">No ad images found</Typography>
      )}
    </>
  );
};

export default OriginalAdImagesTable;
